import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';

/**
 * Franchise-only header (scoped to /franchise route).
 * Layout: BLACK bar with the Ionic Estate Sales Franchise logo on the far left,
 * center-aligned nav, and a large diagonal NEON-GREEN geometric panel on the
 * right side that houses the APPLY NOW CTA. The green panel's left edge is a
 * hard diagonal, matching the supplied design reference. The main site's
 * Header is untouched.
 */
const FranchiseHeader = () => {
  const [open, setOpen] = useState(false);

  const nav = [
    { label: 'Home', href: '/', active: true },
    { label: 'About', href: '#build-lead-grow' },
    { label: 'Opportunity', href: '#why-ionic' },
    { label: 'Support', href: '#everything-you-need' },
    { label: 'FAQ', href: '#path' },
    { label: 'Contact', href: '#final-cta' },
  ];

  return (
    <header
      data-testid="franchise-header"
      className="sticky top-0 z-50 bg-black border-b border-[color:var(--green)]/25 shadow-[0_0_25px_-5px_rgba(25,223,2,0.35)] overflow-hidden relative"
    >
      {/* ===== RIGHT — Diagonal green geometric panel (spans to viewport edge) ===== */}
      <div className="hidden md:block absolute inset-y-0 right-0 w-[240px] lg:w-[300px] z-20">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[color:var(--green)] shadow-[0_0_40px_-6px_rgba(25,223,2,0.7)]"
          style={{ clipPath: 'polygon(28% 0, 100% 0, 100% 100%, 12% 100%)' }}
        />
        <Link
          to="/franchise-apply"
          data-testid="apply-now-btn"
          className="absolute inset-y-0 right-0 flex items-center gap-1.5 pl-14 lg:pl-20 pr-6 lg:pr-8 text-black hover:text-white font-display uppercase tracking-[0.14em] text-[13px] lg:text-[14px] transition-colors"
        >
          Apply Now <ChevronRight className="w-4 h-4" strokeWidth={3} />
        </Link>
      </div>

      <div className="relative max-w-[1440px] mx-auto h-[68px] md:h-[86px] flex items-stretch">
        {/* ===== LEFT — Logo ===== */}
        <a
          href="/franchise"
          data-testid="franchise-logo"
          className="relative z-10 shrink-0 flex items-center pl-4 md:pl-8"
        >
          <img
            src="/franchise/franchise-logo.webp?v=2"
            alt="Ionic Estate Sales Franchise"
            className="h-[46px] md:h-[64px] w-auto select-none block"
            draggable={false}
          />
        </a>

        {/* ===== CENTER — Nav (desktop) ===== */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 flex-1 justify-center pl-6 pr-[280px]">
          {nav.map((n) => (
            <a
              key={n.label}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`relative font-display text-[13.5px] tracking-[0.12em] uppercase transition-colors ${
                n.active ? 'text-white' : 'text-white/85 hover:text-[color:var(--green)]'
              }`}
            >
              {n.label}
              {n.active && (
                <span className="absolute -bottom-1.5 left-0 right-0 h-[2px] rounded-full bg-[color:var(--green)] shadow-[0_0_10px_rgba(25,223,2,0.9)]" />
              )}
            </a>
          ))}
        </nav>

        {/* ===== Mobile toggle ===== */}
        <button
          className="lg:hidden ml-auto mr-4 self-center text-[#19FD02] p-2 relative z-10"
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* ===== Mobile dropdown ===== */}
      {open && (
        <div className="lg:hidden bg-black border-t border-[color:var(--green)]/30 px-6 py-6">
          <div className="flex flex-col gap-4">
            {nav.map((n) => (
              <a
                key={n.label}
                href={n.label === 'Home' ? '/' : n.href}
                onClick={() => setOpen(false)}
                className="font-display uppercase tracking-[0.12em] text-white text-sm"
              >
                {n.label}
              </a>
            ))}
            <Link
              to="/franchise-apply"
              onClick={() => setOpen(false)}
              data-testid="mobile-apply-now-btn"
              className="inline-flex items-center gap-1.5 bg-[color:var(--green)] text-black font-display text-[13px] tracking-[0.10em] uppercase px-5 py-3 rounded-md w-fit"
            >
              Apply Now <ChevronRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default FranchiseHeader;
